import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../api/client';
import { useToast } from '../../components/ui/Toast';
import { DataGrid, type Column } from '../../components/ui/DataGrid';
import { Widget } from '../../components/dashboard/Widget';
import { formatEUR } from '../../lib/format';
import { PageHeader } from '../../components/ui/PageHeader';

interface RevenueRow {
  month: string;
  revenue_ht: string;
  revenue_ttc: string;
  invoice_count: number;
}
interface MarginByBrand {
  brand: string;
  qty_sold: number;
  revenue_ht: string;
  margin_ht: string;
  margin_pct: string;
}

/** Screen 52 — Revenue Dashboard (charts: monthly trend, margin by brand). V1 uses tables; charts in v2. */
export default function RevenueDashboard() {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const [months, setMonths] = useState<RevenueRow[]>([]);
  const [brands, setBrands] = useState<MarginByBrand[]>([]);
  const [today, setToday] = useState<{ revenue_ht: string; invoice_count: number } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [m, b, t] = await Promise.all([
          apiClient.get<{ data: RevenueRow[] }>('/reports/revenue/by-month'),
          apiClient.get<{ data: MarginByBrand[] }>('/reports/margins/by-brand'),
          apiClient.get<{ data: { revenue_ht: string; invoice_count: number } }>(
            '/reports/revenue'
          ),
        ]);
        setMonths(m.data.data);
        setBrands(b.data.data);
        setToday(t.data.data);
      } catch (err) {
        showApiError(err);
      }
    })();
  }, [showApiError]);

  const monthCols: Column<RevenueRow>[] = [
    { key: 'month', header: t('reports.revenue.month') },
    {
      key: 'revenue_ht',
      header: t('reports.revenue.revenueHt'),
      render: (r) => `${formatEUR(Number(r.revenue_ht))}`,
    },
    {
      key: 'revenue_ttc',
      header: t('reports.revenue.revenueTtc'),
      render: (r) => `${formatEUR(Number(r.revenue_ttc))}`,
    },
    { key: 'invoice_count', header: t('reports.revenue.invoices') },
  ];
  const brandCols: Column<MarginByBrand>[] = [
    { key: 'brand', header: t('common.brand') },
    { key: 'qty_sold', header: t('reports.revenue.qtySold') },
    {
      key: 'revenue_ht',
      header: t('reports.revenue.revenueHt'),
      render: (r) => `${formatEUR(Number(r.revenue_ht))}`,
    },
    {
      key: 'margin_ht',
      header: t('reports.revenue.marginHt'),
      render: (r) => `${formatEUR(Number(r.margin_ht))}`,
    },
    {
      key: 'margin_pct',
      header: t('reports.revenue.marginPct'),
      render: (r) => `${Number(r.margin_pct).toFixed(1)} %`,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title={t('reports.revenue.title')} />
      <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
        <Widget
          title={t('reports.revenue.revenueToday')}
          value={today ? `${formatEUR(Number(today.revenue_ht))}` : '…'}
        />
        <Widget title={t('reports.revenue.invoicesToday')} value={today?.invoice_count ?? '…'} />
        <Widget
          title={t('reports.revenue.topBrand')}
          value={brands[0]?.brand ?? '—'}
          detail={brands[0] ? `${Number(brands[0].margin_pct).toFixed(1)} %` : ''}
        />
      </div>
      <section className="space-y-2">
        <h2 className="font-semibold">{t('reports.revenue.monthly')}</h2>
        <DataGrid columns={monthCols} rows={months} rowKey={(r) => r.month} />
      </section>
      <section className="space-y-2">
        <h2 className="font-semibold">{t('reports.revenue.marginByBrand')}</h2>
        <DataGrid columns={brandCols} rows={brands} rowKey={(r) => r.brand} />
      </section>
    </div>
  );
}
